import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, XCircle, RefreshCw, Loader2, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import API from '../services/api';

function FailedRecipients() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    loadFailed();
    return () => clearInterval(pollRef.current);
  }, [id]);

  const loadFailed = async () => {
    setLoading(true);
    try {
      const res = await API.get(`/campaigns/${id}/failed-recipients`);
      const payload = res.data.data;
      setData(payload);
      // Agar page load hote hi campaign already 'processing' mein mila
      // (jaise user beech mein navigate kar gaya tha), to polling resume karo
      if (payload.campaign.status === 'processing') {
        setResending(true);
        startPolling();
      }
    } catch (error) {
      toast.error('Failed to load failed recipients');
    } finally {
      setLoading(false);
    }
  };

  const startPolling = () => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await API.get(`/campaigns/${id}/failed-recipients`);
        const payload = res.data.data;
        setData(payload);
        if (payload.campaign.status !== 'processing') {
          clearInterval(pollRef.current);
          setResending(false);
          toast.success('Resend complete!');
        }
      } catch (error) {
        clearInterval(pollRef.current);
        setResending(false);
      }
    }, 3000);
  };

  const handleResendAll = async () => {
    if (!data || data.recipients.length === 0) return;
    setResending(true);
    const toastId = toast.loading('Starting resend...');
    try {
      await API.post(`/campaigns/${id}/resend-failed`);
      toast.success('Resend started — this may take a moment', { id: toastId });
      startPolling();
    } catch (error) {
      if (error.response?.status === 409) {
        // Kisi aur click/tab se already resend chal raha hai — usी ko track karo
        toast.error('A resend is already in progress — tracking it now', { id: toastId });
        startPolling();
      } else {
        toast.error(error.response?.data?.message || 'Failed to start resend', { id: toastId });
        setResending(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="loading-container" style={{ padding: '60px 0' }}>
        <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#1e3a5f' }} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="dash-page">
        <div className="empty-state">
          <div className="empty-state-icon"><XCircle size={56} color="#cbd5e1" /></div>
          <h3 className="empty-state-title">Campaign Not Found</h3>
          <button onClick={() => navigate('/bulk-message')} className="settings-btn-test" style={{ marginTop: '14px' }}>
            <ArrowLeft size={15} /> Back to Bulk Message
          </button>
        </div>
      </div>
    );
  }

  const { campaign, recipients } = data;

  return (
    <div className="dash-page">
      <div className="dash-header">
        <div>
          <button onClick={() => navigate(`/bulk-message/campaign/${id}`)} className="settings-btn-test" style={{ marginBottom: '12px' }}>
            <ArrowLeft size={15} /> Back to Campaign
          </button>
          <h1 className="dash-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <XCircle size={26} color="#dc2626" /> Failed Recipients — {campaign.name || `Campaign #${campaign.id}`}
          </h1>
          <p className="dash-subtitle">
            {recipients.length} recipient(s) currently failed
          </p>
        </div>
        <button
          onClick={handleResendAll}
          disabled={resending || recipients.length === 0}
          className="settings-btn-save"
          style={{ height: 'fit-content', opacity: (resending || recipients.length === 0) ? 0.6 : 1 }}
        >
          {resending
            ? <><Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> Resending...</>
            : <><RefreshCw size={15} /> Resend All</>}
        </button>
      </div>

      {resending && (
        <div className="dash-card mb-lg" style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
          <p style={{ margin: 0, fontSize: '13.5px', color: '#92400e', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} />
            Resend chal raha hai — jo recipients successful honge wo yahan se apne aap hat jaayenge.
          </p>
        </div>
      )}

      <div className="dash-card">
        {recipients.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><AlertTriangle size={56} color="#22c55e" /></div>
            <h3 className="empty-state-title">No Failed Recipients</h3>
            <p className="empty-state-description">
              {resending ? 'Resend complete — everyone went through successfully.' : 'All recipients were sent successfully.'}
            </p>
            <button onClick={() => navigate(`/bulk-message/campaign/${id}`)} className="settings-btn-test" style={{ marginTop: '14px' }}>
              <ArrowLeft size={15} /> Back to Campaign
            </button>
          </div>
        ) : (
          <div className="table-container table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {recipients.map((r, i) => (
                  <tr key={r.contact_id || i}>
                    <td>{r.phone}</td>
                    <td>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        padding: '3px 10px', borderRadius: '20px', fontSize: '12px',
                        fontWeight: '700', background: '#fee2e2', color: '#991b1b'
                      }}>
                        <XCircle size={12} /> Failed
                      </span>
                    </td>
                    <td style={{ color: '#dc2626', fontSize: '12.5px' }}>{r.error_message || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default FailedRecipients;